import { useState } from 'react'
import type { ChangeEvent, FC } from 'react'
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  format,
  parseISO,
} from 'date-fns'
import { ISO_DATE_FORMAT } from '~/shared/lib/date'
import { FFInput } from '~/shared/ui'
const LETTER_TO_DATE_FN = { d: addDays, w: addWeeks, m: addMonths, y: addYears }

// Add a relative offset to a date, to return another date.
// Offset is specified as a string with optional sign and units (e.g. '3w', '-2d')
// The default unit is days (d)
const offsetToDate = (offset: string): Date | null => {
  const start = new Date()

  if (!offset.toLowerCase().match(/[dwmy]$/)) {
    offset += 'd'
  }

  const intOffset = parseInt(offset.slice(0, -1), 10)
  if (isNaN(intOffset)) return null

  const unit = offset.slice(-1).toLowerCase() as keyof typeof LETTER_TO_DATE_FN
  return LETTER_TO_DATE_FN[unit](start, intOffset)
}

const dateToOffset = (toStr: string): string => {
  // Find the difference between the two dates
  // Assuming we were talking about the beginning of today -> beginning of selected day
  // in local timezone
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  // Make sure that 2021-08-05 is parsed to
  // Date Thu Aug 05 2021 00:00:00 (local time)
  const to = parseISO(toStr)
  to.setHours(0, 0, 0, 0)

  let offsetStr
  const dayDifference = differenceInDays(to, from)
  const absDayDifference = Math.abs(dayDifference)
  const prefix = dayDifference >= 0 ? '+' : '-'

  if (absDayDifference === 365) {
    offsetStr = `${Math.floor(dayDifference / 365)}y`
  } else if (absDayDifference === 30) {
    offsetStr = `${Math.floor(absDayDifference / 30)}m`
  } else {
    offsetStr = `${Math.floor(absDayDifference)}d`
  }

  return `${prefix}${offsetStr}`
}

interface DateAndRelativeDateInputProps {
  disabled?: boolean
  value: string // Empty string or YYYY-MM-DD (@ 00:00:00 local time)
  onChange: (value: string) => void // value is (YYYY-MM-DD @ 00:00:00 local time)
  /** Lay the offset and the date out side by side, the offset at 0.4 of the date's width. */
  split?: boolean
  minDate?: string // YYYY-MM-DD or null
}

// Show local timezone, so that picking dates makes sense to the user
// Callback with YYYY-MM-DD in local timezone. Up to the caller to interpret this
const DateAndRelativeDateInput: FC<DateAndRelativeDateInputProps> = props => {
  const [offset, setOffset] = useState('')
  const [isOffsetDirty, setIsOffsetDirty] = useState(false)

  // The offset text follows the date whenever the caller changes it.
  const [shownValue, setShownValue] = useState<string>()
  if (props.value !== shownValue) {
    setShownValue(props.value)
    setOffset(props.value ? dateToOffset(props.value) : '')
    setIsOffsetDirty(false)
  }

  const handleOffsetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(e.target.value)
    setIsOffsetDirty(true)
  }

  const handleOffsetSelection = () => {
    // Only update the date if the user actually modified the offset
    if (!isOffsetDirty) return
    const offsetDate = offsetToDate(offset)
    const newDateStr = offsetDate ? format(offsetDate, ISO_DATE_FORMAT) : ''
    props.onChange(newDateStr)
    setIsOffsetDirty(false)
  }

  const handleTextInputSelection = (event: ChangeEvent<HTMLInputElement>) => {
    // When a user picks a date, interpret the choice as having been selected in local time
    const dateStr: string = event.target.value // will be formatted as YYYY-MM-DD
    props.onChange(dateStr)
  }

  return (
    <>
      <FFInput
        disabled={props.disabled}
        onChange={handleOffsetChange}
        onBlur={handleOffsetSelection}
        value={offset}
        type="text"
        sx={props.split ? { mr: 1, flex: 0.4 } : undefined}
        placeholder="T+"
      />
      <FFInput
        disabled={props.disabled}
        onChange={handleTextInputSelection}
        value={props.value}
        type="date"
        sx={props.split ? { flex: 1 } : undefined}
        placeholder="Add due date"
        slotProps={{ input: { inputProps: { min: props?.minDate } } }}
      />
    </>
  )
}

export default DateAndRelativeDateInput
